import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// Expo-out. Almost all of the travel is spent in the first third, then the item drifts the last
// few pixels into place — that long tail is what makes it read as surfacing rather than sliding.
const ease = [0.16, 1, 0.3, 1] as const;

/** How far below its slot a match starts. Far enough to be a rise, not a nudge. */
const riseDistance = 36;
const riseDuration = 0.55;

// Past this many items the cascade stops adding delay: a full page of twenty would otherwise
// take over a second to finish arriving, and nothing below the fold is being watched anyway.
const maxCascadeSteps = 6;
const cascadeStep = 0.07;

interface FeedItemRevealProps {
  /** Position in the list. Drives the cascade of a batch that lands together. */
  index: number;
  className?: string;
  children: ReactNode;
}

/**
 * The arrival animation for a match in the card's Feed panel.
 *
 * Matches rise into their slot from below and fade in as they go, so a card whose matches were
 * still being computed fills its feed instead of blinking into place. A batch that lands together
 * rises one after another.
 */
export function FeedItemReveal({ index, className, children }: FeedItemRevealProps) {
  const reduceMotion = useReducedMotion();
  const delay = Math.min(index, maxCascadeSteps) * cascadeStep;

  if (reduceMotion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      // No `layout`: this can sit inside an ancestor that is itself mid shared-layout-morph (the
      // card growing out of its list row), and a layout-tracked child inherits that ancestor's
      // scale correction on top of its own rise animation — the two compound into a visibly
      // distorted, stretched arrival instead of a clean rise.
      initial={{ opacity: 0, y: riseDistance }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{
        duration: riseDuration,
        ease,
        delay,
        // A per-value transition REPLACES the top-level one in motion rather than merging with it,
        // so the override below has to restate the cascade delay or it would start immediately —
        // which reads as the text appearing first and the item sliding up afterwards.
        //
        // Opacity finishes well before the movement does: an item still fading while it drifts the
        // last few pixels looks sluggish, one already solid looks like it is settling.
        opacity: { duration: riseDuration * 0.6, ease: 'easeOut', delay },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
